export default class Vector2 {

    x: number;
    y: number;

    static get zero() { return new Vector2(0, 0); }
    static get one() { return new Vector2(1, 1); }
    static get up() { return new Vector2(0, -1); }
    static get right() { return new Vector2(1, 0); }
    static get down() { return new Vector2(0, 1); }
    static get left() { return new Vector2(-1, 0); }

    static fromObject(obj: {x: number; y: number}) {
        return new Vector2(obj.x, obj.y);
    }

    static IsVector2(obj: any) {
        return obj instanceof Vector2 ? true : false;
    }

    static fromRadians(theta: number) {
        return new Vector2(
            Math.cos(theta),
            Math.sin(theta)
        );
    }

    static fromAngleDegrees(angle: number) {
        return Vector2.fromRadians(angle * Math.PI / 180);
    }
    
    static distance(a: Vector2, b: Vector2) {
        return b.sub(a).magnitude;
    }

    static angle(a: Vector2, b: Vector2 = Vector2.zero) {

        // const vec = a.sub(b); //vec that goes from a to b
        // return Math.atan2(vec.y, vec.x);
        let tA = Math.atan2(a.y, a.x);
        let tB = Math.atan2(b.y, b.x);

        return Math.abs(tA - tB);
    }

    static angleDegrees(a :Vector2, b: Vector2 = Vector2.zero) {
        return Vector2.angle(a, b) * 180 / Math.PI;
    }

    static dotProduct(a: Vector2, b: Vector2) {
        return a.x * b.x + a.y * b.y;
    }

    static crossProduct(a: Vector2, b: Vector2) {
        return a.x * b.y - a.y * b.x;
    }

    static clampMagnitude(v: Vector2, max: number) {
        if (v.magnitude > max) return v.normalized.scale(max);
        return v.clone();
    }

    static lerp(a: Vector2, b: Vector2, t: number) {
        return b.sub(a).scale(t).add(a);
    }

    static berp(t: number, ...points: Vector2[]) {
        if (points.length < 2) throw new Error("Need at least two points");
        
        while (points.length > 2) {
            const newLi: Vector2[] = [];

            for (let i = 0; i < points.length-1; i++) {
                newLi.push(this.lerp(points[i], points[i+1], t));
            }

            points = newLi;
        }

        return this.lerp(points[0], points[1], t);
    }

    get squareMagnitude() { return this.x * this.x + this.y * this.y; }
    get magnitude() { return Math.sqrt(this.squareMagnitude); }
    get normalized() {
        if (this.squareMagnitude === 0) return Vector2.right;
        return this.scale(1/this.magnitude);
    }

    get angle() {
        return Math.atan2(this.y, this.x);
    }
    
    constructor(x: number, y: number) {
        this.x = x;
        this.y = y;
    }
    
    clone() { return new Vector2(this.x, this.y); }

    add(v: Vector2) {
        return new Vector2(this.x + v.x, this.y + v.y);
    }
    sub(v: Vector2) {
        return new Vector2(this.x - v.x, this.y - v.y);
    }
    scale(n: number) {
        return new Vector2(this.x * n, this.y * n);
    }
    multiply(v: Vector2) {
        return new Vector2(this.x * v.x, this.y * v.y);
    }

    normalize() {
        const magnitude = this.magnitude;
        this.x /= magnitude;
        this.y /= magnitude;
    }

    perpendicular() {

        return new Vector2(this.y, -this.x);

    }

    equals(v: Vector2) {
        return this.x === v.x && this.y === v.y;
    }

    rotate(theta: number) {
        theta += this.angle;
        return Vector2.fromRadians(theta).scale(this.magnitude);
    }

    toObject() {
        return {
            x: this.x,
            y: this.y
        }
    }

}